import _ from 'lodash';
import React, { useEffect, useState } from 'react';

import {
    Envs,
    Modules,
    commonConfig as commonDefaultConfig,
    useBaseUrl,
    useConfig,
    useEnvContext,
    useLocale,
} from 'scbk-services/config';
import { useConfigStore } from 'scbk-services/config/stores/configStore.ts';
import { HttpProvider } from 'scbk-services/http';
import { clearMockConfigs, initMockProvider } from 'scbk-services/mock';

import packageJson from '../../../package.json';

import useSystemContext from 'scbk-services/config/hooks/useSystemContext';
import type { HttpProviderProps } from 'scbk-services/http/providers/HttpProvider';
import type { CommonConfigType, ConfigValue } from 'scbk-services/types/config/config-types';

interface BaseModuleProps {
    module: Modules;
    moduleConfig: Record<string, ConfigValue>;
    commonConfig: CommonConfigType;
    subModulesConfig?: Record<string, Record<string, ConfigValue>>;
    mockConfig?: Record<string, (schema: unknown, request: unknown) => string | object | undefined>;
    metaEnv: ImportMetaEnv;
    children?: React.ReactNode;
    httpProviderProps?: Omit<HttpProviderProps, 'baseUrl' | 'children'>;
}

export const BaseModule: React.FC<BaseModuleProps> = ({
    module,
    moduleConfig,
    commonConfig,
    subModulesConfig,
    mockConfig = {},
    metaEnv,
    children,
    httpProviderProps,
}) => {
    const [isInitialized, setIsInitialized] = useState(false);
    const { getConfig, setConfig } = useConfig();
    const currentEnv = useConfigStore((state) => state.currentEnv);

    const { setMarket, setEnv, getEnv, getPreDefinedEnv, getPreDefinedMarket } = useEnvContext();
    const { setSystem, getPreDefinedSystem } = useSystemContext();
    const { setLocale, getPreDefinedLocale } = useLocale();

    const mockInitializedRef = React.useRef(false);

    const { jwtUseMode } = getConfig(Modules.COMMON);
    const { getExpBaseUrl, getKonghBaseUrl } = useBaseUrl();

    const baseUrl =
        'KONG' === jwtUseMode
            ? getKonghBaseUrl(getEnv() === Envs.MOCK)
            : getExpBaseUrl(getEnv() === Envs.MOCK);

    const memoizedSetupMocks = React.useCallback(async () => {
        if (getEnv() === Envs.MOCK) {
            if (!mockInitializedRef.current) {
                console.log('Setup Mocks for Mock Environment');
                const mockOptions = {
                    enabled: true,
                    useAbsolutePath: window.location.protocol === 'file:',
                };

                initMockProvider(module, mockConfig, mockOptions);

                mockInitializedRef.current = true;
            }
        } else {
            if (window.server) {
                console.log('Shutdown Mocks for Non-Mock Environment');
                clearMockConfigs();
                mockInitializedRef.current = false;
                console.log('Mock Server Shutdown ✖️');
            }
        }
    }, [currentEnv, mockConfig]);

    const memoizedInitConfigs = React.useCallback(() => {
        try {
            console.log(
                `The [${module}] module is using sc-services version:`,
                packageJson.version,
            );

            // Initialize common config
            if (_.isEmpty(getConfig(Modules.COMMON))) {
                const preDefinedMarket = getPreDefinedMarket(metaEnv);
                const preDefinedEnv = getPreDefinedEnv(metaEnv);
                const preDefinedLocale = getPreDefinedLocale();
                const preDefinedSystem = getPreDefinedSystem(metaEnv);
                setMarket(preDefinedMarket);
                setEnv(preDefinedEnv);
                setLocale(preDefinedLocale);
                setSystem(preDefinedSystem);
                setConfig(Modules.COMMON, { ...commonDefaultConfig, ...commonConfig });

                console.log(`Initialized the <common> config when loading the [${module}] ⚙️ ✅`, {
                    env: preDefinedEnv,
                    market: preDefinedMarket,
                    locale: preDefinedLocale,
                    commonConfig: getConfig(Modules.COMMON),
                    system: preDefinedSystem,
                    timestamp: new Date().toISOString(),
                });
            }

            // Initialize module config
            if (_.isEmpty(getConfig(module))) {
                setConfig(module, moduleConfig);
                console.log(
                    `Initialized the <${module}> config when loading the [${module}] ⚙️ ✅`,
                    {
                        config: getConfig(module),
                        timestamp: new Date().toISOString(),
                    },
                );
            }

            // Initialize subModuleConfig
            for (const subModule in subModulesConfig) {
                if (_.isEmpty(getConfig(subModule))) {
                    setConfig(subModule, subModulesConfig[subModule as keyof typeof Modules]);
                    console.log(
                        `Initialized the <${subModule}> config when loading the [${module}] ⚙️ ✅`,
                        {
                            config: getConfig(subModule),
                            timestamp: new Date().toISOString(),
                        },
                    );
                }
            }

            setIsInitialized(true);
        } catch (error) {
            console.error('Failed to initialize configs ❌:', error);
            setIsInitialized(false);
        }
    }, []);

    useEffect(() => {
        memoizedSetupMocks();
    }, [memoizedSetupMocks]);

    useEffect(() => {
        memoizedInitConfigs();
    }, [memoizedInitConfigs]);

    if (!isInitialized) {
        return null;
    }

    return (
        <HttpProvider
            baseUrl={baseUrl}
            onRestDefaultError={httpProviderProps?.onRestDefaultError}
            onRestForbiddenHandler={httpProviderProps?.onRestForbiddenHandler}
            onRestUnauthorizedError={httpProviderProps?.onRestUnauthorizedError}
        >
            {children}
        </HttpProvider>
    );
};

export const getCssPrefix = (module: Modules) => {
    return `mf__css-${module}`;
};
